import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Grid,
  Stack,
  Paper,
  Button,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import {
  KingBed,
  Bathtub,
  Balcony,
  SquareFoot,
  Power,
  PriceChange,
  Layers,
  LocationOn,
  Opacity,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import EnquiryModal from '../CustomerInteractions/EnquiryModal';
import { useSelector } from 'react-redux';
import SecondaryButton from '../common/SecondaryButton';
import GradientButton from '../common/GradiantButton';
import CallIcon from "@mui/icons-material/Call";
import ContactModal from "../contact/ContactModal";
import LoginModal from '../common/LoginModal';
import {
  formatIndianCurrencyWithSymbol,
  resolvePropertyPrice,
} from '../../utils/priceFormat';

const VillaDetails = ({ propertyData }) => {
  const { isKyc, isAuthenticated ,isVerified} = useSelector((state) => state.user);
  const userId = useSelector((state) => state.user.userId);
  const navigate = useNavigate();
  const rawData = propertyData[0]?.ExtraData;
  const theme = useSelector((data) => data.themeValue.colors)
  const [openModal, setOpenModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showKycSnackbar, setShowKycSnackbar] = useState(false);

  // Production getIndependentHouseById returns MarketPrice as "₹3.2 Cr" (already abbreviated).
  // Number("₹3.2 Cr") is NaN — prior fixes missed that. Parse Cr/L/K + numeric fallbacks.
  const propertyValue = resolvePropertyPrice(propertyData[0]);

  const villaData = [
    { icon: <KingBed fontSize="small" />, label: "Bedrooms", value: rawData?.Bedrooms || "NA" },
    { icon: <Bathtub fontSize="small" />, label: "Bathrooms", value: rawData?.Bathrooms || "NA" },
    { icon: <Layers fontSize="small" />, label: "Floors", value: rawData?.NoOfFloors || "NA" },
    { icon: <Balcony fontSize="small" />, label: "Balcony", value: rawData?.Balcony || "NA" },
    { icon: <Power fontSize="small" />, label: "Power Backup", value: rawData?.PowerBackUp || "NA" },
    { icon: <Opacity fontSize="small" />, label: "Water Supply", value: rawData?.WaterSupply || "NA" },
    { icon: <SquareFoot fontSize='small' />, label: "Size In SqFt", value: propertyData[0]?.SizeInSqFt, kind: "size" },
    { icon: <PriceChange fontSize='small' />, label: "Property Value", value: propertyValue, kind: "price" }
  ];

  const handleOpen = () => {
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    setOpenModal(true);
  }

  const handleModalClose = () => {
    setOpenModal(false);
  }

  // Add contact button handler
  const handleContactClick = () => {
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    if (propertyData && propertyData[0]) {
      const propertyInfo = {
        PropertyName: propertyData[0]?.PropertyName,
        Address: propertyData[0]?.Address,
        City: propertyData[0]?.City,
        // Add any other properties you want to pass to ContactModal
      };
      setSelectedProperty(propertyInfo);
      setShowContactModal(true);
    }
  };

  const formatSizeInSqFt = (size) => {
    if (size === null || size === undefined || size === "") return "—";

    const cleaned = String(size).replace(/[,\s]/g, "").replace(/sq\.?ft\.?/i, "");
    if (!cleaned || cleaned.toUpperCase() === "NA" || cleaned.toUpperCase() === "N/A") {
      return "—";
    }

    const num = Number(cleaned);
    if (!Number.isFinite(num)) return String(size);

    return num.toLocaleString("en-IN");
  };

  const formatTileValue = (item) => {
    if (item.kind === "price") {
      return formatIndianCurrencyWithSymbol(item.value) || "—";
    }
    if (item.kind === "size") {
      return formatSizeInSqFt(item.value);
    }
    return item.value;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRadius: 2,
        backgroundColor: theme.background,
      }}
    >
      {/* Contact Modal */}
      {showContactModal && selectedProperty && (
        <ContactModal
          open={showContactModal}
          onClose={() => setShowContactModal(false)}
          property={selectedProperty}
        />
      )}

      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 0.5,
            color: theme.text,
          }}
        >
          {propertyData[0]?.PropertyName}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Box>
            <LocationOn sx={{ color: theme.normal_icon }} fontSize='large' />
          </Box>
          <Box>
            <Typography variant="body2" sx={{
              color: theme.text, fontSize: "13px", overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>{propertyData[0]?.Address || " "}</Typography>
            <Typography variant='body3' sx={{ color: theme.text }}>{propertyData[0]?.City || " "}</Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 1, backgroundColor: theme.secondary_text_color }} />
      </Box>

      <Grid
        container
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'divider',
            borderRadius: 3,
          },
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {villaData.map((item, index) => (
          <Grid xs={6} key={index}>
            <Box
              elevation={0}
              sx={{
                p: { xs: 0, sm: 1 },
                height: { xs: "5rem", sm: '4rem' },
                width: { xs: "8rem", sm: '11rem' },
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                border: '1px solid',
                borderColor: theme.borderColor,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  color: theme.normal_icon,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: "40%"
                }}
              >
                {item.icon}
              </Box>
              <Stack sx={{ width: "60%" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{
                    lineHeight: 1.3,
                    fontSize: "80%",
                    color: theme.text,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatTileValue(item)}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1, color: theme.text }}
                >
                  {item.label}
                </Typography>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ backgroundColor: theme.secondary_text_color }} />

      {propertyData[0]?.Status !== 'sale Initiated' && propertyData[0].Status !== 'Sold' && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "column" },
            gap: 1,
            textAlign: "center",
            justifyContent: "center"
          }}
        >
          <Box sx={{ width: "100%", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 2 }}>
            {isAuthenticated && (<SecondaryButton
              text="Enquiry"
              onClick={handleOpen}
              width='47%'
            />)}


            <SecondaryButton
              text={
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CallIcon sx={{ mr: 1, fontSize: 25 }} />
                  Contact
                </Box>
              }
              width='47%'
              onClick={handleContactClick} // Use the new handler function
              style={{
                "& svg": {
                  color: "primary.main",
                  transition: "color 0.3s ease",
                },
                "&:hover": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "& svg": {
                    color: "white",
                  },
                },
              }}
            />

            <SecondaryButton
              text="Site Visit"
              onClick={() => {

                if (!userId) {
                  setShowLoginModal(true);
                  return;
                }
                navigate("/schedulesitevisit", {
                  state: {
                    propertyData: propertyData[0].Pid,
                    type: propertyData[0].PropertyType,
                    data:propertyData
                  },
                })
              }
              }
              width='47%'
            />

          </Box>

          {openModal && (
            <EnquiryModal
              open={openModal}
              onClose={handleModalClose}
              propertyData={propertyData[0].Pid}
              type={propertyData[0].PropertyType}
            />
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showKycSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowKycSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor: theme.background,
            color: theme.text,
            width: '400px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            fontSize: '16px',
          },
        }}
        message="Please complete your KYC to book the property."
        action={
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => {
              setShowKycSnackbar(false);
              navigate("/KYC");
            }}
            sx={{ marginLeft: 2 }}
          >
            Update KYC
          </Button>
        }
      />
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
    </Paper>
  );
};

export default VillaDetails;